import { datadogRum } from '@datadog/browser-rum';
import type { ErrorInfo } from 'react';
import type { RootOptions } from 'react-dom/client';

const claimedErrors = new WeakSet<object>();

const isObject = (value: unknown): value is object => typeof value === 'object' && value !== null;

/**
 * Returns true the first time an error is seen, so each React error reaches RUM once whichever
 * of its reporters (a boundary, the root callbacks, RUM's console capture) gets to it first.
 */
export const claimReactError = (error: unknown): boolean => {
  if (!isObject(error)) return true;
  if (claimedErrors.has(error)) return false;
  claimedErrors.add(error);
  return true;
};

export const isReactErrorClaimed = (error: unknown): boolean =>
  isObject(error) && claimedErrors.has(error);

const reportReactError = (customType: string) => (error: unknown, errorInfo: ErrorInfo) => {
  if (claimReactError(error)) {
    datadogRum.addError(error, { customType, componentStack: errorInfo.componentStack });
  }
  if (process.env.LOCAL === 'true') console.error(error, errorInfo.componentStack);
};

const reportCaughtError = reportReactError('reactCaught');

export const rootErrorOptions: RootOptions = {
  // React 19 calls this just before the catching boundary's componentDidCatch, in the same
  // commit. Deferring lets a boundary that reports its own errors claim them with its context.
  onCaughtError: (error, errorInfo) => {
    queueMicrotask(() => {
      reportCaughtError(error, errorInfo);
    });
  },
  onUncaughtError: reportReactError('reactUncaught'),
  onRecoverableError: reportReactError('reactRecoverable')
};
